import { supabase } from "../supabaseClient";
import { getCachedUser } from '../contexts/AuthContext';

function isMissingColumnOrTableError(err) {
  const msg = String(err?.message || "").toLowerCase();
  return (
    msg.includes("column") ||
    msg.includes("does not exist") ||
    msg.includes("relation") ||
    msg.includes("schema cache")
  );
}

async function updateOrInsertByKey({ table, keyColumn, keyValue, payload }) {
  if (!supabase) return;

  try {
    const { data: updated, error: updateErr } = await supabase
      .from(table)
      .update(payload)
      .eq(keyColumn, keyValue)
      .select()
      .maybeSingle();

    if (updateErr) {
      if (isMissingColumnOrTableError(updateErr)) return;
      throw updateErr;
    }

    if (updated) return;

    const { error: insertErr } = await supabase
      .from(table)
      .insert({ [keyColumn]: keyValue, ...payload })
      .select()
      .maybeSingle();

    if (insertErr) {
      if (isMissingColumnOrTableError(insertErr)) return;
      // If it already exists (race), ignore.
      if (String(insertErr?.message || "").toLowerCase().includes("duplicate")) return;
      throw insertErr;
    }
  } catch (err) {
    // Best-effort: avatar persistence should not block the user.
    console.warn(`[avatarPersistence] ${table} update failed`, err);
  }
}

export async function persistAvatarForCurrentUser({
  avatarUrl,
  avatarEmoji,
  displayName,
  country,
} = {}) {
  if (!supabase) return { ok: false, error: new Error("Supabase not configured") };

  // Get current user from centralized auth state (no direct supabase.auth call)
  const user = getCachedUser();

  if (!user?.id) return { ok: false, error: new Error("Not authenticated") };

  const userId = user.id;

  const normalized = {
    avatar_url: avatarUrl === undefined ? undefined : avatarUrl,
    avatar_emoji: avatarEmoji === undefined ? undefined : avatarEmoji,
    display_name: displayName === undefined ? undefined : displayName,
    country: country === undefined ? undefined : country,
  };

  const profilePayload = {
    ...(normalized.display_name !== undefined ? { display_name: normalized.display_name } : {}),
    ...(normalized.country !== undefined ? { country: normalized.country } : {}),
    ...(normalized.avatar_url !== undefined ? { avatar_url: normalized.avatar_url } : {}),
    ...(normalized.avatar_emoji !== undefined ? { avatar_emoji: normalized.avatar_emoji } : {}),
    ...(user?.email ? { email: user.email } : {}),
  };

  // Base profile (AuthContext reads this as `profiles` by id)
  await updateOrInsertByKey({
    table: "profiles",
    keyColumn: "id",
    keyValue: userId,
    payload: profilePayload,
  });

  // Visitor profile (AuthContext reads this as `visitor_profiles` by user_id)
  await updateOrInsertByKey({
    table: "visitor_profiles",
    keyColumn: "user_id",
    keyValue: userId,
    payload: {
      ...(normalized.display_name !== undefined ? { display_name: normalized.display_name } : {}),
      ...(normalized.avatar_url !== undefined ? { avatar_url: normalized.avatar_url } : {}),
      ...(normalized.avatar_emoji !== undefined ? { avatar_emoji: normalized.avatar_emoji } : {}),
    },
  });

  // Public user profile used by messaging/user lookup
  await updateOrInsertByKey({
    table: "users_public",
    keyColumn: "id",
    keyValue: userId,
    payload: {
      ...(normalized.display_name !== undefined ? { display_name: normalized.display_name } : {}),
      ...(normalized.country !== undefined ? { country: normalized.country } : {}),
      ...(normalized.avatar_url !== undefined ? { avatar_url: normalized.avatar_url } : {}),
      ...(normalized.avatar_emoji !== undefined ? { avatar_emoji: normalized.avatar_emoji } : {}),
      ...(user?.email ? { email: user.email } : {}),
    },
  });

  // Studio record (AuthContext reads this from `studios` by owner_user_id)
  if (normalized.avatar_url !== undefined || normalized.avatar_emoji !== undefined) {
    try {
      const studioPatch = {
        ...(normalized.avatar_url !== undefined ? { avatar_url: normalized.avatar_url } : {}),
        ...(normalized.avatar_emoji !== undefined ? { avatar_emoji: normalized.avatar_emoji } : {}),
      };

      const { error: studioErr } = await supabase
        .from("studios")
        .update(studioPatch)
        .eq("owner_user_id", userId);

      if (studioErr && !isMissingColumnOrTableError(studioErr)) {
        console.warn("[avatarPersistence] studios update failed", studioErr);
      }
    } catch (err) {
      console.warn("[avatarPersistence] studios update exception", err);
    }
  }

  return { ok: true, error: null };
}
